import { FilterTextField, ClearFiltersButton } from "../../../common/ClearControls";
import { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  Box,
  Grid,
  MenuItem,
  Typography
} from '@mui/material';
import MainCard from "../../components/MainCard";
import ScoreBarChart from './ScoreBarChart';
import VideoCompletionsChart from './VideoCompletionsChart';
import HighestVideoTable from './VideoTable';
import FeedbackTable from './FeedbackTable';
import Loader from '../../components/Loader';
import PDFExportComponent from "../../components/PDFExportComponent";
import Sidebar from "../../menu-items/Sidebar";

const PatientPage = () => {
  const [practiceList, setPracticeList] = useState([{ label: "All practices", value: "all" }]);
  const [practice, setPractice] = useState('all');
  const [clinicianList, setClinicianList] = useState([{ label: "All clinicians", value: "all" }]);
  const [clinician, setClinician] = useState('all');
  const [locationList, setLocationList] = useState([{ label: "All locations", value: "all" }]);
  const [location, setLocation] = useState('all');

  const filters = {
    location: location,
    practice: practice,
    clinician: clinician
  };
  const chartIds = [
    { id: 'maincard_patient_video_completions', singleRow: true, scale: 1.1 },
    { id: 'box_patient_feedback', singleRow: true, scale: 0.5 },
    { id: 'patient-engagement-FeedbackTable', singleRow: true, ignoreMaxHeight: true, scale: 1 },
    { id: 'box_average_video_engagement', singleRow: true, scale: 0.8 },
    { id: 'patient-engagement-VideoTable', singleRow: true, ignoreMaxHeight: true, scale: 1 },
  ];
  const fileName = 'report-patient-engagement.pdf';

  useEffect(() => {
    let isMounted = true;
    const fetchLocationList = async () => {
      try {
        const locationURL = `${process.env.REACT_APP_BASE_URL}admin/dropdown/locations`;
        const token = axios.defaults.headers.common.Authorization;
           const config = {
          headers: {
            Authorization: `${token}`,
          },
        };
        const locationResponse = await axios.get(locationURL, config);
        if (isMounted) {
          if (locationResponse.data && locationResponse.data.length > 0) {
            const updatedLocationList = [
              { label: "All locations", value: "all" },
              ...locationResponse.data
            ];
            setLocationList(updatedLocationList);
            setLocation(updatedLocationList[0].value);
          }
        }
      } catch (error) {
        console.error('Error fetching location data: ', error);
      }
    };

    fetchLocationList();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
  const fetchPractices = async () => {
    try {
      const token = axios.defaults.headers.common.Authorization;

      const baseUrl = process.env.REACT_APP_BASE_URL;
      const url = `${baseUrl}admin/dropdown/practices_with_id`;

       const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

      const response = await axios.get(url, config);

      if (response.data && response.data.length > 0) {
        const updatedPracticeList = [
          { label: 'All practices', value: 'all' },
          ...response.data,
        ];
        setPracticeList(updatedPracticeList);
        setPractice(updatedPracticeList[0].value);
        console.log('Fetched practices:', updatedPracticeList);
      }
    } catch (error) {
      console.error('Error fetching practices:', error);
    }
  };

  fetchPractices();
}, []);

  useEffect(() => {
  const fetchUsers = async () => {
    try {
      const token = axios.defaults.headers.common.Authorization;
      const baseUrl = process.env.REACT_APP_BASE_URL;
      const url = `${baseUrl}admin/dropdown/users`;

     const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

      const response = await axios.get(url, config);

      if (response.data && response.data.length > 0) {
        const updatedUserList = [
          { label: 'All clinicians', value: 'all' },
          ...response.data,
        ];
        setClinicianList(updatedUserList);
        setClinician(updatedUserList[0].value);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  fetchUsers();
}, []);


  if (locationList.length <= 1 || practiceList.length <= 1 || clinicianList.length <= 1) {
    return <Loader />
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Sidebar */}
        <Grid item xs={12} md={3} lg={2}>
          <Sidebar />
        </Grid>

        {/* Filters and Score Chart */}
        <Grid item xs={12} md={9} lg={10}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <MainCard>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    Patient Engagement
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <FilterTextField active={location !== "all"} onClear={() => setLocation("all")} clearLabel="Clear location filter"
                      id="location-filter"
                      label="Location"
                      size="small"
                      select
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      fullWidth
                    >
                      {locationList.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </FilterTextField>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <FilterTextField active={practice !== "all"} onClear={() => setPractice("all")} clearLabel="Clear practice filter"
                      id="practice-filter"
                      label="Practice"
                      size="small"
                      select
                      value={practice}
                      onChange={(e) => setPractice(e.target.value)}
                      fullWidth
                    >
                      {practiceList.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </FilterTextField>
                  </Box>
                  <Box>
                    <FilterTextField active={clinician !== "all"} onClear={() => setClinician("all")} clearLabel="Clear clinician filter"
                      id="clinician-filter"
                      label="Clinician"
                      size="small"
                      select
                      value={clinician}
                      onChange={(e) => setClinician(e.target.value)}
                      fullWidth
                    >
                      {clinicianList.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </FilterTextField>
                  <ClearFiltersButton onClear={() => { setLocation("all"); setPractice("all"); setClinician("all"); }} />
                  </Box>
                </Box>
              </MainCard>
            </Grid>

            <Grid item xs={12} md={8}>
              <MainCard>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  Patient Engagement Score
                </Typography>
                <ScoreBarChart
                  practice={practice}
                  clinician={clinician}
                  location={location}
                />
              </MainCard>
            </Grid>

            {/* Video Completions Chart */}
            <Grid item xs={12} md={4}>
              <MainCard id='maincard_patient_video_completions'>
                <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
                  Patient Video Completions
                </Typography>
                <VideoCompletionsChart
                  practice={practice}
                  clinician={clinician}
                  location={location}
                />
              </MainCard>
            </Grid>

            {/* Average Video Engagement */}
            <Grid item xs={12} md={4}>
              <MainCard>
                <Typography id='box_average_video_engagement' variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
                  Average Video Engagement
                </Typography>
                <HighestVideoTable
                  practice={practice}
                  clinician={clinician}
                  location={location}
                />
              </MainCard>
            </Grid>

            {/* Patient Feedback */}
            <Grid item xs={12} md={4}>
              <MainCard>
                <Typography id='box_patient_feedback' variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
                  Patient Feedback
                </Typography>
                <FeedbackTable
                  practice={practice}
                  clinician={clinician}
                  location={location}
                />
              </MainCard>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <PDFExportComponent
                  filters={filters}
                  chartIds={chartIds}
                  fileName={fileName}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
};

export default PatientPage;
